import axios from 'axios';
import { hasApiAccess } from '../services/privilegeService';
import API_CONFIG from '../config/api.config';

// Create axios instance with base URL
const axiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Add request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Get token from localStorage using the correct key
    const token = localStorage.getItem('accessToken');

    // If token exists, add it to request headers
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const deviceId = localStorage.getItem('device_id');
    const sessionId = localStorage.getItem('session_id');
    if (deviceId) {
      config.headers['X-Device-Id'] = deviceId;
    }
    if (sessionId) {
      config.headers['X-Session-Id'] = sessionId;
    }
    const companyId = localStorage.getItem('userCompanyId');
    const url = config.url || '';
    if (companyId && !url.includes('/superadmin/')) {
      config.headers['X-Tenant-ID'] = companyId;
    }

    // Replace any hardcoded URLs with the configured base URL and path
    if (config.url && config.url.includes('juharitrading.com')) {
      // Extract the path portion of the URL
      const url = new URL(config.url);
      const path = url.pathname;
      // Remove any existing /api prefix since it will be added below
      const cleanPath = path.replace(/^\/api/, '');
      console.log(`Replacing hardcoded URL: ${config.url} with path: ${cleanPath}`);
      config.url = cleanPath;
    }

    // Check if URL is absolute (starts with http or https)
    const isAbsoluteUrl = /^https?:\/\//i.test(config.url);

    // No need to add API prefix since BASE_URL already includes /api
    // Just log the final URL for debugging
    if (!isAbsoluteUrl) {
      console.log(`Making API request to: ${API_CONFIG.BASE_URL}${config.url}`);
    }

    // Check if user has permission for this endpoint using the privilege service
    try {
      if (!hasApiAccess(config.url)) {
        console.log(`API access denied for ${config.url}`);
        // Reject the request if user doesn't have permission
        return Promise.reject({
          response: {
            status: 403,
            data: {
              message: 'You do not have permission to access this resource'
            }
          }
        });
      }
    } catch (error) {
      console.error('Error checking API access:', error);
      // If there's an error in the permission check, allow the request
      // to avoid blocking legitimate requests due to permission system errors
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track shown alerts to prevent repeated popups
const shownAlerts = new Map();
const ALERT_TIMEOUT = 5000; // 5 seconds - don't show same alert again within this time
const SESSION_EXPIRED_ALERT_KEY = 'session-expired-global';
let isHandlingSessionExpiry = false;

// Add response interceptor to handle errors with friendly popup messages
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      if (error.response) {
        const { status } = error.response;
        const message =
          error.response.data?.message ||
          error.response.data?.error ||
          'Something went wrong. Please try again.';

        // Create unique key for this error to prevent duplicates.
        // Session-expiry (401) must be global, not per-endpoint, to avoid repeated popups.
        const errorKey = status === 401
          ? SESSION_EXPIRED_ALERT_KEY
          : `${status}-${error.config?.url || 'unknown'}`;
        const now = Date.now();

        // Check if we've shown this alert recently
        const lastShown = shownAlerts.get(errorKey);
        if (lastShown && (now - lastShown) < ALERT_TIMEOUT) {
          // Don't show alert again - just log it
          console.warn(`[Axios] Suppressing duplicate ${status} alert for: ${error.config?.url}`);
          return Promise.reject(error);
        }

        // Mark this alert as shown
        shownAlerts.set(errorKey, now);

        // Clean up old entries (older than timeout)
        for (const [key, timestamp] of shownAlerts.entries()) {
          if (now - timestamp > ALERT_TIMEOUT) {
            shownAlerts.delete(key);
          }
        }

        // Show friendly popup messages (only once per endpoint per 5 seconds)
        switch (status) {
          case 401:
            // Multiple requests can fail with 401 at the same time; handle logout once.
            if (isHandlingSessionExpiry) {
              return Promise.reject(error);
            }
            isHandlingSessionExpiry = true;

            alert('Your session has expired. Please login again.');

            // Redirect immediately after user clicks OK.
            localStorage.clear();
            if (window.location?.protocol === 'file:') {
              window.location.hash = '#/';
            } else {
              window.location.href = '/';
            }
            break;
          case 403:
            // Log 403 errors but don't show intrusive alerts as requested by user
            console.warn(`[Axios] Permission denied for ${error.config?.url} - handling gracefully`);
            break;
          case 404:
            // Don't show alert for 404 - it's usually handled by the component
            console.warn(`[Axios] Resource not found: ${error.config?.url}`);
            break;
          case 500:
            console.error('[Axios] Internal Server Error (500):', message);
            // Intrusive alert removed as requested by user
            break;
          default:
            // Only show alert for unexpected errors that are not client errors (4xx)
            if (status >= 400 && status < 500) {
              console.warn(`[Axios] Client error ${status}: ${message}`);
            } else {
              console.error(`[Axios] Error ${status}: ${message}`);
            }
        }
      } else {
        // Network error or request not sent
        // In desktop app (Electron), suppress alert - offline fallbacks will handle it
        const isDesktopApp = typeof window !== 'undefined' && (
          window.electronAPI ||
          (window.navigator?.userAgent?.toLowerCase?.().includes('electron') ?? false)
        );
        if (!isDesktopApp) {
          const networkErrorKey = 'network-error';
          const lastShown = shownAlerts.get(networkErrorKey);
          const now = Date.now();
          if (!lastShown || (now - lastShown) > ALERT_TIMEOUT) {
            alert('No network connection. Please check your internet and try again.');
            shownAlerts.set(networkErrorKey, now);
          }
        } else {
          console.warn('[Axios] Network error (desktop may use offline data):', error.message);
        }
      }
    } catch (e) {
      // Swallow popup errors to avoid breaking flow
      console.error('[Axios] Error in response interceptor:', e);
    }
    return Promise.reject(error);
  }
);

export default axiosInstance; 
